import {
  Component,
  ElementRef,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Modal } from 'flowbite';
import { AnimationOptions } from 'ngx-lottie';
import { IBase, IClient, ITown } from 'src/app/models';
import { UserRole } from 'src/app/models/enums';
import {
  LiteralService,
  StorageService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-upload-excel',
  templateUrl: './modal-upload-excel.component.html',
  styleUrl: './modal-upload-excel.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalUploadExcelComponent {
  readonly id = input<string>();
  readonly modal = input<Modal>();
  readonly loading = input<boolean>(false);
  readonly loadingPercentage = input<number>(0);
  readonly loadingDetail = input<string>('');
  readonly action = output<any>();

  readonly form = viewChild<ElementRef>('form');
  readonly fileUpload = viewChild<ElementRef>('fileUpload');
  public formGroup: FormGroup = new FormGroup({});

  public towns: any[] = [];
  public clients: any[] = [];

  public file?: File;

  public images = images;

  public selectTown = true;

  public snapToRoad = false;

  public importing: AnimationOptions = {
    path: '/assets/animations/importing.json',
  };

  public UserRole = UserRole;
  public adminRole: UserRole;

  constructor(
    public literalService: LiteralService,
    private usersService: UsersService,
    private storageService: StorageService,
    public utilsService: UtilsService,
  ) {}

  async ngOnInit() {
    this.adminRole = await this.storageService.getRole();
    await this.usersService.getPossibleClientTowns().then((res) => {
      res.towns.sort(
        (firstItem: IBase, secondItem: IBase) => firstItem.id! - secondItem.id!,
      );
      res.clients.forEach((client: IClient) => {
        this.clients.push({
          id: client.id,
          value: client.id,
          label: client.name,
        });
      });
    });
    this.formGroup = new FormGroup({});
    this.setControls();
  }

  onFileSelected(event: any, type: string) {
    const file: File = event.target.files[0];
    if (!file) return;

    const fileName = file.name;

    this.file = file;

    const control = this.formGroup.get(type);
    control?.setValue(fileName);
    control?.markAsTouched();
    control?.updateValueAndValidity();

    this.setControls();
  }

  async setControls() {
    this.formGroup.addControl(
      'excel',
      new FormControl('', [Validators.required]),
    );
    if (this.selectTown) {
      this.formGroup.addControl(
        'townId',
        new FormControl('', [Validators.required]),
      );
    } else {
      this.formGroup.addControl(
        'townName',
        new FormControl('', [Validators.required]),
      );
    }
    this.formGroup.addControl(
      'clientId',
      new FormControl('', [Validators.required]),
    );
    if (this.clients.length === 1) {
      this.formGroup.get('clientId')?.setValue(this.clients[0].id);
      await this.loadTowns(this.clients[0].id!);
    }
  }

  async setClientId(event: any) {
    this.formGroup.get('clientId')?.setValue(event.value);
    await this.loadTowns(event.value);
  }

  private async loadTowns(clientId: number) {
    this.towns = [];
    // Towns without routes yet are valid import targets
    await this.usersService.getTownsByClient([clientId], false).then((res) => {
      const towns = res as ITown[];
      towns.forEach((town: ITown) => {
        this.towns.push({ id: town.id, value: town.id, label: town.name });
      });
    });
  }

  setTownId(event: any) {
    this.formGroup.get('townId')?.setValue(event.value);
  }

  setTownName(event: any) {
    this.formGroup.get('townName')?.setValue(event.target.value);
  }

  changeCreationType(create?: boolean) {
    this.selectTown = !this.selectTown;
    if (create) {
      this.formGroup.removeControl('townId');
      this.formGroup.addControl(
        'townName',
        new FormControl('', [Validators.required]),
      );
    } else {
      this.formGroup.removeControl('townName');
      this.formGroup.addControl(
        'townId',
        new FormControl('', [Validators.required]),
      );
    }
  }

  changeSnapToRoad(event: any) {
    this.snapToRoad = event.target.checked;
  }

  reset() {
    this.file = undefined;
    const fileInput = this.fileUpload()?.nativeElement;
    if (fileInput) fileInput.value = '';
    this.selectTown = true;
    this.snapToRoad = false;
    this.towns = [];
    this.formGroup = new FormGroup({});
    this.setControls();
  }

  onSubmit() {
    let data = {
      file: this.file,
      clientId: this.formGroup.get('clientId')?.value,
      snapToRoad: this.snapToRoad,
    };
    Object.assign(
      data,
      this.selectTown
        ? { townId: this.formGroup.get('townId')?.value }
        : { townName: this.formGroup.get('townName')?.value },
      this.snapToRoad,
    );
    this.action.emit(data);
  }
}
